import React, { useState, useEffect, useLayoutEffect, useContext } from "react";
import { useSelector } from "react-redux";
import ApiNode from "../../../app/api/admin/node/ApiNode";
import Page from "../../modules/page";
import { ContextContent } from "../../modules/content/contentContext";
import Table from "../../modules/table";
import ListHeaderItem from "../../modules/list/__header-item";
import ListBodyItem from "../../modules/list/__body-item";
import Select from "../../components/inputs/select";
import InputNumber from "../../components/inputs/number/number";
import Button from "../../components/button";
import InputCheckbox from "../../components/inputs/checkbox";
import Loader from "../../components/loader";
import "./styles.css";

// Сводная страница «Установка устройств»: все готовые (status=provisioned) ноды сразу,
// на каждой — выбор тарифа+кол-ва и кнопка «Добавить», без перехода на карточку ноды
// (тот же вызов ApiNode.Deploy, что там же на карточке — см. nodes/show.js::NodeActions).
// Без авто-подбора тарифа — ручной выбор из state.option.deviceTypes.
//
// Ручное добавление: лимит max_devices ноды здесь НЕ проверяется (skip_limit) — оператор
// ставит устройство сверх боевого набора осознанно. Галочка «Опустить после установки» —
// контейнер после установки сразу останавливается (данные сохраняются), поднимают его позже.
export default function DeviceInstallsIndex({ ...other }) {
    return <Page {...other}>
        <DeviceInstallsContent/>
    </Page>;
}

function DeviceInstallsContent() {
    const contextContent = useContext(ContextContent);
    const deviceTypes = useSelector((state) => state.option.deviceTypes);
    const canEdit = contextContent.checkPermission('edit');

    const [_nodes, set_nodes] = useState(null);

    const loadNodes = async () => {
        // per_page=200: без него Index() отдавал бы только первую страницу (20 нод),
        // остальные готовые ноды на странице просто не появлялись.
        const api = ApiNode.Index({ status: 'provisioned', per_page: 200 });
        const data = await api.getResponse();
        set_nodes(data ?? []);
    };

    useLayoutEffect(() => {
        contextContent.setTitle('Установка устройств');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadNodes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const header = [
        <ListHeaderItem key="id" name="id" isSort={false}>ID</ListHeaderItem>,
        <ListHeaderItem key="name" name="name" isSort={false}>Имя</ListHeaderItem>,
        <ListHeaderItem key="wan_ip" name="wan_ip" isSort={false}>Хост</ListHeaderItem>,
        <ListHeaderItem key="devices_count" name="devices_count" isSort={false}>Устройств</ListHeaderItem>,
        <ListHeaderItem key="install" name="install" isSort={false}>Добавить устройства</ListHeaderItem>,
    ];

    const renderRow = (row) => [
        <ListBodyItem key="id">{row.id}</ListBodyItem>,
        <ListBodyItem key="name">{row.name}</ListBodyItem>,
        <ListBodyItem key="wan_ip">{row.wan_ip}</ListBodyItem>,
        <ListBodyItem key="devices_count">{row.devices_count ?? 0}</ListBodyItem>,
        <ListBodyItem key="install">
            {canEdit ? <NodeInstallRow nodeId={row.id} deviceTypes={deviceTypes}/> : null}
        </ListBodyItem>,
    ];

    // null — список ещё грузится. Иначе Table рисовал бы «Нет данных», а это здесь читается
    // как «готовых нод нет» — прямо противоположный вывод, ради которого страницу и открывают.
    if (_nodes === null) return <Loader/>;

    if (_nodes.length === 0) {
        return <div className="device-installs__empty">Нет готовых (provisioned) нод.</div>;
    }

    return <Table header={header} rows={_nodes} renderRow={renderRow}/>;
}

// Одна строка: тариф + кол-во + кнопка. Своё локальное состояние на ноду — соседние
// строки друг другу не мешают.
function NodeInstallRow({ nodeId, deviceTypes }) {
    const [_deviceTypeId, set_deviceTypeId] = useState('');
    const [_count, set_count] = useState('1');
    const [_stopAfterInstall, set_stopAfterInstall] = useState(false);
    const [_isBusy, set_isBusy] = useState(false);
    const [_result, set_result] = useState(null);

    const handleAdd = async () => {
        if (_deviceTypeId === '') {
            new ApiNode().addMessages('Выберите тариф', 'warning');
            return;
        }
        set_isBusy(true);
        set_result(null);
        try {
            const api = ApiNode.Deploy(nodeId, {
                device_type_id: _deviceTypeId,
                count: _count,
                skip_limit: true,
                stop_after_install: _stopAfterInstall,
            });
            const data = await api.getResponse();
            if (data && data.result) {
                const created = (data.created ?? []).length;
                const suffix = _stopAfterInstall ? ' — после установки будут опущены' : '';
                api.addMessages(`Поставлено в очередь: ${created}${suffix}`, 'success');
                set_result(`+${created}`);
            }
        } finally {
            set_isBusy(false);
        }
    };

    return <div className="device-installs__row">
        <Select className="input_wf device-installs__type" options={deviceTypes} value={_deviceTypeId} isNull={true}
            onChange={(e) => set_deviceTypeId(e.target.value)} hasLabel={false}>Тариф</Select>
        <InputNumber className="input_wf device-installs__count" value={_count} min={1} max={20}
            onChange={(e) => set_count(e.target.value)} hasLabel={false}>Количество</InputNumber>
        <InputCheckbox className="device-installs__stopped" value={_stopAfterInstall}
            onChange={(e) => set_stopAfterInstall(e.target.checked)}>Опустить после установки</InputCheckbox>
        <Button className="btn-success" disabled={_isBusy} onClick={handleAdd}>
            {_isBusy ? 'Добавление…' : 'Добавить'}
        </Button>
        {_result ? <span className="device-installs__result">{_result}</span> : null}
    </div>;
}
